const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const getToken = () => localStorage.getItem('token');

export const ApiError = class extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
};

const handleResponse = async (response) => {
  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login?session=expired';
    return;
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new ApiError(
      body.error || body.message || 'An error occurred',
      response.status,
      response.statusText
    );
    error.deactivated = body.deactivated;
    error.unverified = body.unverified;
    throw error;
  }
  return response.json();
};

const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export const api = {
  get: async (endpoint) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Network error', 0, 'NETWORK_ERROR');
    }
  },

  post: async (endpoint, data) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Network error', 0, 'NETWORK_ERROR');
    }
  },

  put: async (endpoint, data) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Network error', 0, 'NETWORK_ERROR');
    }
  },

  delete: async (endpoint) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Network error', 0, 'NETWORK_ERROR');
    }
  },
};

export const productsApi = {
  getAll: () => api.get('/api/products'),
  getNew: () => api.get('/api/products/new'),
  getExclusive: () => api.get('/api/products/exclusive'),
  getById: (id) => api.get(`/api/products/${id}`),
  getBySubcategory: (subcategorySlug, limit) => {
    let url = `/api/products?subcategory=${subcategorySlug}`;
    if (limit) url += `&limit=${limit}`;
    return api.get(url);
  },
  getByCategory: (slug, limit) => {
    let url = `/api/products?category=${encodeURIComponent(slug)}`;
    if (limit) url += `&limit=${limit}`;
    return api.get(url);
  },
  search: (query) => api.get(`/api/products/search/${encodeURIComponent(query)}`),
};

export const authApi = {
  register: (data) => api.post('/api/users/register', data),
  login: (data) => api.post('/api/users/login', data),
  getProfile: () => api.get('/api/users/profile'),
  updateProfile: (data) => api.put('/api/users/profile', data),
  googleAuth: (data) => api.post('/api/users/auth/google', data),
};

export const ordersApi = {
  create: (orderData) => api.post('/api/orders', orderData),
  getById: (id) => api.get(`/api/orders/${id}`),
  getAll: () => api.get('/api/orders'),
  trackByCode: (code) => api.get(`/api/orders/track/${encodeURIComponent(code)}`),
};

export const cartApi = {
  get: () => api.get('/api/cart'),
  addItem: (item) => api.post('/api/cart/add', item),
  updateItem: (cartItemId, quantity) => api.put(`/api/cart/update/${cartItemId}`, { quantity }),
  removeItem: (itemId) => api.delete(`/api/cart/remove/${itemId}`),
  clear: () => api.delete('/api/cart/clear'),
};

export const settingsApi = {
  getPublic: () => api.get('/api/settings/public'),
};

export const categoriesApi = {
  getAll: () => api.get('/api/products/categories'),
  getBySlug: (slug) => api.get(`/api/products/categories/${encodeURIComponent(slug)}`),
};

export const brandsApi = {
  getAll: () => api.get('/api/products/brands'),
};

export const addressesApi = {
  getAll: () => api.get('/api/users/addresses'),
  add: (data) => api.post('/api/users/addresses', data),
  update: (id, data) => api.put(`/api/users/addresses/${id}`, data),
  delete: (id) => api.delete(`/api/users/addresses/${id}`),
};

export const mpesaApi = {
  initiateStkPush: (data) => api.post('/api/mpesa/stk-push', data),
  getStatus: async (checkoutRequestId) => {
    const result = await api.get(`/api/mpesa/status/${checkoutRequestId}`);
    // Return order data if payment was successful
    return result;
  },
  getPaymentByOrderId: (orderId) => api.get(`/api/mpesa/order/${orderId}`),
};

export const newsletterApi = {
  subscribe: (data) => api.post('/api/newsletter', data),
  checkStatus: (email) => api.get(`/api/newsletter/status/${encodeURIComponent(email)}`),
  unsubscribe: (data) => api.put('/api/newsletter/unsubscribe', data),
};

export const instagramApi = {
  getPosts: () => api.get('/api/instagram'),
};

export const giftcardsApi = {
  check: (code) => api.post('/api/giftcards/check', { code }),
  getByCode: (code) => api.get(`/api/giftcards/${encodeURIComponent(code)}`),
  redeem: (code, orderId) => api.post('/api/giftcards/redeem', { code, orderId }),
  getMyGiftcards: () => api.get('/api/giftcards/my/giftcards'),
};

export const bannersApi = {
  getByCategory: (slug) => api.get(`/api/banners/category/${slug}`),
  getHeroBanners: () => api.get('/api/banners/hero'),
  getPromoBanners: () => api.get('/api/banners/promo'),
};

export const careersApi = {
  getPositions: () => api.get('/api/careers/positions'),
  apply:        (data) => api.post('/api/careers/apply', data),
};

export const storesApi = {
  getAll: () => api.get('/api/stores'),
};

export default api;